import { AlertTriangle, Check, Copy } from 'lucide-react';
import { useMemo } from 'react';
import type { CopyPreviousWeekResult } from '@obraplan/core/application/use-cases/planning/planning-use-cases';
import type { BoardActivity, BoardResource } from '@obraplan/core/domain/planning/weekly-board';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';

/**
 * Copiar a semana anterior — **com a lista antes de gravar**.
 *
 * A confirmação anterior prometia que "o que não couber é pulado, com o motivo listado" e
 * nunca listava nada: numa semana já planejada, todas as linhas eram puladas, o aviso
 * dizia que a semana anterior não tinha alocações e a tela ficava igual. Quem clicava
 * concluía, com razão, que o botão não funcionava.
 *
 * Agora a pré-visualização roda antes — o caso de uso já a oferecia, sem gravar nada — e
 * cada linha aparece com destino e situação. Quando nada cabe, o diálogo diz **por quê**
 * em vez de deixar o silêncio explicar.
 */
export interface CopyWeekDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ausente = a pré-visualização ainda está carregando. */
  preview: CopyPreviousWeekResult | undefined;
  resources: readonly BoardResource[];
  activities: readonly BoardActivity[];
  isPending: boolean;
  onConfirm: () => void;
}

const WEEKDAY = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const;

/** `"seg 24/08"` — o dia de destino, no formato que o quadro usa no cabeçalho. */
function formatDay(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return date;
  const weekday = WEEKDAY[new Date(Date.UTC(year, month - 1, day)).getUTCDay()] ?? '';
  return `${weekday} ${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
}

export function CopyWeekDialog({
  open,
  onOpenChange,
  preview,
  resources,
  activities,
  isPending,
  onConfirm,
}: CopyWeekDialogProps) {
  const resourceName = useMemo(
    () => new Map(resources.map((resource) => [resource.resourceId as string, resource.name])),
    [resources],
  );
  const activityLabel = useMemo(
    () => new Map(activities.map((activity) => [activity.activityId as string, activity.label])),
    [activities],
  );

  const lines = preview?.lines ?? [];
  const willCopy = preview?.copiedCount ?? 0;
  const willSkip = preview?.skippedCount ?? 0;
  const quarterHours = lines
    .filter((line) => !line.skipped)
    .reduce((sum, line) => sum + line.quarterHours, 0);

  const sourceWeek =
    preview === undefined ? 'a semana anterior' : `a semana ${String(preview.sourceIsoWeek)}`;
  const plural = (count: number, singular: string, many: string): string =>
    `${String(count)} ${count === 1 ? singular : many}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Copiar a semana anterior</DialogTitle>
          <DialogDescription>
            {preview === undefined
              ? 'Conferindo o que cabe nesta semana…'
              : preview.sourceHasAllocations
                ? `${plural(lines.length, 'alocação', 'alocações')} n${sourceWeek}. Cada uma cai no mesmo dia da semana, sete dias adiante — a segunda vira segunda.`
                : `Não há nada a copiar: ${sourceWeek} não tem nenhuma alocação.`}
          </DialogDescription>
        </DialogHeader>

        {preview !== undefined && preview.sourceHasAllocations && (
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={willCopy > 0 ? 'ok' : 'neutral'} icon={<Check aria-hidden="true" />}>
              {willCopy} a copiar · {Hours.ofQuarters(quarterHours).format()}
            </Badge>
            {willSkip > 0 && (
              <Badge tone="warn" icon={<AlertTriangle aria-hidden="true" />}>
                {plural(willSkip, 'não cabe', 'não cabem')}
              </Badge>
            )}
          </div>
        )}

        {willCopy === 0 && willSkip > 0 && (
          <p className="rounded-(--radius-card) border border-(--color-state-warn) bg-(--color-surface-muted) p-3 text-sm">
            <strong>Nenhuma linha cabe nesta semana.</strong> Esta semana já tem horas planejadas
            nos mesmos dias, e a cópia não grava acima da jornada sem justificativa. Para repetir
            mesmo assim, apague ou reduza o que já está lançado — ou lance as horas pelo painel,
            onde a exceção de capacidade pode ser autorizada.
          </p>
        )}

        {lines.length > 0 && (
          <div className="max-h-80 overflow-y-auto rounded-(--radius-card) border border-(--color-border-subtle)">
            <table className="w-full text-sm">
              <caption className="sr-only">
                Linhas da semana anterior e o que acontece com cada uma
              </caption>
              <thead className="sticky top-0 bg-(--color-surface-raised)">
                <tr>
                  <th scope="col" className="p-2 text-left font-medium">
                    Recurso
                  </th>
                  <th scope="col" className="p-2 text-left font-medium">
                    Atividade
                  </th>
                  <th scope="col" className="p-2 text-left font-medium">
                    Destino
                  </th>
                  <th scope="col" className="p-2 text-right font-medium">
                    Horas
                  </th>
                  <th scope="col" className="p-2 text-left font-medium">
                    Situação
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr
                    key={`${line.resourceId}-${line.activityId}-${line.date}-${String(index)}`}
                    className={cn(line.skipped && 'text-(--color-content-muted)')}
                  >
                    <td className="p-2">{resourceName.get(line.resourceId) ?? '—'}</td>
                    <td className="p-2">
                      {activityLabel.get(line.activityId) ?? 'Atividade encerrada'}
                    </td>
                    <td className="p-2">{formatDay(line.date)}</td>
                    <td className="tabular p-2 text-right">
                      {Hours.ofQuarters(line.quarterHours).format()}
                    </td>
                    <td className="p-2">
                      {line.skipped ? (
                        <span className="text-(--color-state-warn)">
                          {line.conflictDescription ?? line.reason ?? 'Pulada'}
                        </span>
                      ) : (
                        <span className="text-(--color-state-ok)">Copiar</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={isPending || preview === undefined || willCopy === 0}
            onClick={onConfirm}
          >
            <Copy aria-hidden="true" />
            {isPending
              ? 'Copiando…'
              : willCopy === 0
                ? 'Nada a copiar'
                : `Copiar ${plural(willCopy, 'alocação', 'alocações')}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
